import { Link, useParams } from '@tanstack/react-router';
import type { ComponentProps, FC } from 'react';

import { DEFAULT_LOCALE } from '~/utils/i18n';

type BaseLinkProps = ComponentProps<typeof Link>;

interface LocalizedLinkProps extends Omit<BaseLinkProps, 'to' | 'params'> {
  to: string;
  params?: Record<string, unknown>;
}

const ensureAbsolutePath = (path: string) =>
  path.startsWith('/') ? path : `/${path}`;

const ensureLocalePrefixedPath = (path: string) => {
  const normalized = ensureAbsolutePath(path);
  if (normalized.startsWith('/$locale')) {
    return normalized;
  }
  if (normalized === '/') {
    return '/$locale';
  }
  return `/$locale${normalized}`;
};

export const LocalizedLink: FC<LocalizedLinkProps> = ({
  to,
  params,
  ...rest
}) => {
  // Get current locale from route params, fallback to default
  const routeParams = useParams({ strict: false });
  const currentLocale = (routeParams as Record<string, string>)?.locale ?? DEFAULT_LOCALE;

  const target = ensureLocalePrefixedPath(to) as BaseLinkProps['to'];

  // Merge current locale with any provided params
  const mergedParams = {
    locale: currentLocale,
    ...params,
  };

  return <Link {...rest} params={mergedParams as unknown as BaseLinkProps['params']} to={target} />;
};
